import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonIconComponent, ITagStats, StatsComponent as TecStatsComponent } from '@tecalisdev/infinity';
import { Subscription } from 'rxjs/internal/Subscription';
import { finalize } from 'rxjs/internal/operators/finalize';
import { ModalCurlRequestComponent } from '../../../shared/components/modal-curl-request/modal-curl-request.component';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { formatFilterTable } from '../../../core/utils/utils';
import { ITotalProcessDateBody, ITotalProcessDateResponse } from '../../models/panel.models';
import { PanelCurlService } from '../../services/panel-curl.service';
import { PanelHelperService } from '../../services/panel-helper.service';
import { PanelService } from '../../services/panel.service';

@Component({
	selector: 'app-stats',
	imports: [TranslatePipe, TecStatsComponent, ButtonIconComponent, ModalCurlRequestComponent],
	templateUrl: './stats.component.html',
	styleUrl: './stats.component.scss',
})
export class StatsComponent {
	private panelService = inject(PanelService);
	private panelHelperService = inject(PanelHelperService);
	protected rolePermissions = inject(RolePermissionsService);
	private panelCurlService = inject(PanelCurlService);
	private destroyRef = inject(DestroyRef);
	private request?: Subscription;

	public stats?: ITotalProcessDateBody['totals'];
	public readonly loading = signal(false);
	public readonly curlModalOpen = signal(false);
	public readonly curlModalTitleKey = signal('PANEL.CURL_MODAL.TITLE');

	constructor() {
		this.destroyRef.onDestroy(() => this.request?.unsubscribe());
		effect(() => {
			const rawFilters = this.panelHelperService.currentFilters();
			if (rawFilters) {
				const filters = { ...formatFilterTable(rawFilters) };
				this.loadStats(filters);
			}
		});
	}

	public openCurlModal(titleKey: string): void {
		this.curlModalTitleKey.set(titleKey);
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	private loadStats(filters: any): void {
		this.request?.unsubscribe();
		this.loading.set(true);
		this.request = this.panelService
			.getTotalProcessDate(filters)
			.pipe(finalize(() => this.loading.set(false)))
			.subscribe({
				next: (response: ITotalProcessDateResponse) => {
					const totals = response?.data?.totals;
					if (totals) {
						this.stats = totals;
					}
				},
				error: () => {
					this.stats = undefined;
				},
			});
	}

	public getTotalProcessDateCurl(): string {
		const rawFilters = this.panelHelperService.currentFilters();
		if (!rawFilters) return '';
		const filters = { ...formatFilterTable({ ...rawFilters }) };
		return this.panelCurlService.buildTotalProcessDateCurl(filters);
	}

	public getTagRounded(delta: number | null): ITagStats {
		if (delta === null) {
			return { icon: 'icon-trending-up', label: '0%' };
		}
		const percentage = ((delta / 100) * 100).toFixed(0) + '%';
		if (delta > 0) {
			return { icon: 'icon-trending-up', label: percentage };
		}
		if (delta < 0) {
			return { icon: 'icon-trending-down', label: percentage, styling: 'red' };
		}
		return { icon: 'icon-trending-up', label: percentage };
	}
}
